import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { api } from '@/lib/api'
import { formatRelativeDate, getChatTypeLabel, getStatusColor, getStatusLabel, cn } from '@/lib/utils'
import { UserIcon } from '@heroicons/react/24/outline'

interface Chat {
  _id: string
  userId: {
    _id: string
    email: string
    name?: string
  }
  type: string
  status: string
  context?: {
    levelId?: {
      number: number
      titleRu: string
    }
  }
  lastMessageAt: string
  unreadCount: number
}

export default function ChatsPage() {
  const navigate = useNavigate()
  const [filter, setFilter] = useState<string>('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  
  const { data, isLoading } = useQuery({
    queryKey: ['chats', filter, statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (filter) params.append('type', filter)
      if (statusFilter) params.append('status', statusFilter)
      
      const res = await api.get<{ data: { chats: Chat[] } }>(`/admin/chats?${params}`)
      return res.data.data.chats
    },
  })
  
  const { data: unreadData } = useQuery({
    queryKey: ['chats-unread'],
    queryFn: async () => {
      const res = await api.get<{ data: { totalUnread: number } }>('/admin/chats/unread')
      return res.data.data
    },
    refetchInterval: 30000,
  })
  
  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-white">Чаты</h1>
          {unreadData && unreadData.totalUnread > 0 && (
            <span className="px-2.5 py-1 bg-red-500 text-white text-sm font-medium rounded-full">
              {unreadData.totalUnread} непрочитанных
            </span>
          )}
        </div>
      </div>
      
      {/* Filters */}
      <div className="card">
        <div className="flex gap-4">
          <select
            className="w-48"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option value="">Все типы</option>
            <option value="quiz">Квиз AI</option>
            <option value="support">Поддержка</option>
            <option value="private_club">Приватный клуб</option>
          </select>
          
          <select
            className="w-48"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">Все статусы</option>
            <option value="active">Активные</option>
            <option value="resolved">Решённые</option>
            <option value="archived">Архив</option>
          </select>
        </div>
      </div>
      
      {/* Chats list */}
      <div className="space-y-2">
        {isLoading ? (
          <div className="card text-center py-8 text-gray-400">
            Загрузка...
          </div>
        ) : data?.length === 0 ? (
          <div className="card text-center py-8 text-gray-400">
            Чаты не найдены
          </div>
        ) : (
          data?.map((chat) => (
            <div
              key={chat._id}
              onClick={() => navigate(`/chats/${chat._id}`)}
              className={cn(
                'card cursor-pointer hover:bg-gray-700/50 transition-colors',
                chat.unreadCount > 0 && 'border-primary-500/50'
              )}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center">
                    <UserIcon className="w-5 h-5 text-gray-400" />
                  </div>
                  
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-white">
                        {chat.userId?.email || 'Удалённый пользователь'}
                      </span>
                      {chat.unreadCount > 0 && (
                        <span className="px-2 py-0.5 bg-primary-500 text-white text-xs rounded-full">
                          {chat.unreadCount}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      <span className="badge-blue">{getChatTypeLabel(chat.type)}</span>
                      {chat.context?.levelId && (
                        <span>Уровень {chat.context.levelId.number}</span>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="text-right">
                  <span className={getStatusColor(chat.status)}>
                    {getStatusLabel(chat.status)}
                  </span>
                  <p className="text-sm text-gray-500 mt-1">
                    {formatRelativeDate(chat.lastMessageAt)}
                  </p>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}
